import { useState } from 'react';
import { Store, Copy, Check, Plug, Loader2 } from 'lucide-react';
import { PageHeader } from '@/components/shared/page-header';
import { LocationBadge } from '@/components/shared/location-badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { PasswordInput } from '@/components/ui/password-input';
import { useLocationStore } from '@/stores/location-store';
import { toast } from '@/stores/toast-store';
import { cn } from '@/lib/utils';
import {
  useWooCommerceConfig,
  useUpdateWooCommerceConfig,
  useTestWooCommerceConnection,
  type WooCommerceStoreConfig,
  type WooCommerceConfigInput,
} from '@/hooks/use-woocommerce-config';

type SyncMode = 'interval' | 'daily' | 'scheduled';

export function OnlineStorePage() {
  const { selectedLocationId } = useLocationStore();
  const { data, isLoading, error } = useWooCommerceConfig(selectedLocationId);

  if (!selectedLocationId) {
    return (
      <div className="space-y-6">
        <PageHeader title="Online Store" description="WooCommerce online store integration" />
        <p className="text-sm text-muted-foreground">Select a location to configure its online store.</p>
      </div>
    );
  }

  if ((error as { status?: number } | null)?.status === 403) {
    return (
      <div className="space-y-6">
        <PageHeader title="Online Store" description="WooCommerce online store integration" />
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            Online Store is not enabled for this location. Contact your administrator to enable it.
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Online Store" description="WooCommerce online store integration" />
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      </div>
    );
  }

  // Remount the form per location so its initial state re-seeds from the freshly
  // loaded config without a state-syncing effect.
  return <OnlineStoreForm key={selectedLocationId} locationId={selectedLocationId} config={data?.data ?? null} />;
}

function OnlineStoreForm({ locationId, config }: { locationId: string; config: WooCommerceStoreConfig | null }) {
  const update = useUpdateWooCommerceConfig(locationId);
  const testConnection = useTestWooCommerceConnection(locationId);

  // Seeded once from the loaded config (component is keyed by location).
  const [storeUrl, setStoreUrl] = useState(config?.storeUrl ?? '');
  const [syncMode, setSyncMode] = useState<SyncMode>(config?.syncMode ?? 'interval');
  const [syncIntervalSec, setSyncIntervalSec] = useState(config?.syncIntervalSec ?? 300);
  const [syncDailyTimePst, setSyncDailyTimePst] = useState(config?.syncDailyTimePst ?? '02:00');
  // Credentials are write-only: blank means "keep existing".
  const [consumerKey, setConsumerKey] = useState('');
  const [consumerSecret, setConsumerSecret] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [copied, setCopied] = useState(false);

  const webhookUrl = `${window.location.origin.replace(/:\d+$/, '')}/api/v1/webhook/woocommerce/${locationId}`;

  const buildPayload = (overrides: Partial<WooCommerceConfigInput> = {}): WooCommerceConfigInput => {
    const payload: WooCommerceConfigInput = {
      storeUrl: storeUrl.trim(),
      syncMode,
      syncIntervalSec,
      syncDailyTimePst: syncMode === 'daily' ? syncDailyTimePst : null,
      ...overrides,
    };
    if (consumerKey.trim()) payload.consumerKey = consumerKey.trim();
    if (consumerSecret.trim()) payload.consumerSecret = consumerSecret.trim();
    if (webhookSecret.trim()) payload.webhookSecret = webhookSecret.trim();
    return payload;
  };

  const handleSave = async () => {
    try {
      await update.mutateAsync(buildPayload());
      setConsumerKey('');
      setConsumerSecret('');
      setWebhookSecret('');
      toast.success('Online store settings saved');
    } catch (e) {
      toast.error((e as { message?: string })?.message || 'Failed to save settings');
    }
  };

  const handleTest = async () => {
    try {
      const res = await testConnection.mutateAsync({
        storeUrl: storeUrl.trim() || undefined,
        consumerKey: consumerKey.trim() || undefined,
        consumerSecret: consumerSecret.trim() || undefined,
      });
      if (res.data.success) toast.success(res.data.message);
      else toast.error(res.data.message);
    } catch (e) {
      toast.error((e as { message?: string })?.message || 'Connection test failed');
    }
  };

  const handleToggleSync = async () => {
    const next = !config?.isActive;
    try {
      await update.mutateAsync(buildPayload({ isActive: next }));
      toast.success(next ? 'Live sync enabled' : 'Live sync paused');
    } catch (e) {
      toast.error((e as { message?: string })?.message || 'Failed to update sync');
    }
  };

  const copyWebhook = () => {
    navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Online Store" description="Connect this location to its WooCommerce store">
        <LocationBadge />
      </PageHeader>

      {/* Live sync status */}
      <Card>
        <CardContent className="flex items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <Store className="h-5 w-5 text-primary" />
            <div>
              <div className="text-sm font-medium">Live sync</div>
              <div className="text-[11px] text-muted-foreground">
                {config?.isActive
                  ? 'Products and stock sync with your WooCommerce store.'
                  : 'Sync is paused. Save credentials, then enable to go live.'}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={handleToggleSync}
            disabled={!config || update.isPending}
            className={cn(
              'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50',
              config?.isActive ? 'bg-primary' : 'bg-gray-200',
            )}
          >
            <span
              className={cn(
                'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                config?.isActive ? 'translate-x-6' : 'translate-x-1',
              )}
            />
          </button>
        </CardContent>
      </Card>

      {/* Store connection */}
      <Card>
        <CardHeader>
          <h3 className="text-sm font-semibold">Store connection</h3>
          <p className="text-[11px] text-muted-foreground">
            Each location has its own WooCommerce store and domain. Enter this location's store URL and REST API
            credentials (WooCommerce → Settings → Advanced → REST API).
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="storeUrl">Store URL</Label>
            <Input
              id="storeUrl"
              placeholder="https://shop.my-location.com"
              value={storeUrl}
              onChange={(e) => setStoreUrl(e.target.value)}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="ck">Consumer key</Label>
              <PasswordInput
                id="ck"
                placeholder={config?.hasCredentials ? '•••••••• (saved)' : 'ck_…'}
                value={consumerKey}
                onChange={(e) => setConsumerKey(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="cs">Consumer secret</Label>
              <PasswordInput
                id="cs"
                placeholder={config?.hasCredentials ? '•••••••• (saved)' : 'cs_…'}
                value={consumerSecret}
                onChange={(e) => setConsumerSecret(e.target.value)}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={handleTest} disabled={testConnection.isPending}>
              {testConnection.isPending ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <Plug className="mr-1.5 h-4 w-4" />
              )}
              Test connection
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Webhooks */}
      <Card>
        <CardHeader>
          <h3 className="text-sm font-semibold">Webhooks</h3>
          <p className="text-[11px] text-muted-foreground">
            Add this URL as a webhook in WooCommerce (Order created/updated, Product updated) so stock changes reflect
            here in near real time. Set the webhook secret to match below.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Webhook delivery URL</Label>
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate rounded-md bg-muted px-3 py-2 text-xs">{webhookUrl}</code>
              <Button variant="ghost" size="sm" onClick={copyWebhook} aria-label="Copy webhook URL">
                {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          <div>
            <Label htmlFor="ws">Webhook secret</Label>
            <PasswordInput
              id="ws"
              placeholder={config?.hasWebhookSecret ? '•••••••• (saved)' : 'Shared secret'}
              value={webhookSecret}
              onChange={(e) => setWebhookSecret(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Sync cadence */}
      <Card>
        <CardHeader>
          <h3 className="text-sm font-semibold">Scheduled sync</h3>
          <p className="text-[11px] text-muted-foreground">A periodic safety-net sync in addition to webhooks.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="mode">Mode</Label>
              <select
                id="mode"
                className="h-9 w-full rounded-md border border-border bg-white px-3 text-sm"
                value={syncMode}
                onChange={(e) => setSyncMode(e.target.value as SyncMode)}
              >
                <option value="interval">Every N seconds</option>
                <option value="daily">Once daily (Pacific)</option>
              </select>
            </div>
            {syncMode === 'interval' ? (
              <div>
                <Label htmlFor="interval">Interval (seconds)</Label>
                <Input
                  id="interval"
                  type="number"
                  min={60}
                  value={syncIntervalSec}
                  onChange={(e) => setSyncIntervalSec(Number(e.target.value))}
                />
              </div>
            ) : (
              <div>
                <Label htmlFor="daily">Daily time (HH:MM, Pacific)</Label>
                <Input
                  id="daily"
                  placeholder="02:00"
                  value={syncDailyTimePst}
                  onChange={(e) => setSyncDailyTimePst(e.target.value)}
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={update.isPending}>
          {update.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
          Save settings
        </Button>
      </div>
    </div>
  );
}
